/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

define([
	'../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'./DashboardThemeAPI',
	'../../../lib/@waca/core-client/js/core-client/utils/ClassFactory',
	'../../../dashboard/data/ThemeDefinition',
	'../../../dashboard/data/ThemeDefinitionAPI',
	'./ThemeProviderAPI'
], (APIFactory, DashboardThemeAPI, ClassFactory, ThemeDefinition, ThemeDefinitionAPI, ThemeProviderAPI) => {

	const SYSTEM_THEME_COLLECTION_ID = 'com.ibm.bi.dashboard.dashboardTheme.system';
	/**
	 * @class
	 * @implements DashboardThemeAPI
	 * @hideconstructor
	 * @classdesc default implementation of the DashboardThemeAPI
	 */
	class DashboardTheme {

		constructor() {
			this._themes = {};
			this._getThemeDefinition = null;
			this._themeProvider = this;
			this._api = APIFactory.createAPI(this,[DashboardThemeAPI]);
		}

		/**
		 * it is required in order to be defined as a dashboard feature
		 * @returns {Object} instance of the API implementation
		 */
		getAPI() {
			return this._api;
		}

		initialize(glassContext) {
			// for the glass less app .. figure out a way to have this without depending on glass context..
			// PRINT POC will fail without the try/catch and swallowing the error here
			try {
				this._logger = glassContext.getCoreSvc('.Logger');
				return glassContext.appController.findCollection(SYSTEM_THEME_COLLECTION_ID).then((collection) => {
					if( Array.isArray(collection) && collection.length > 0) {
						const themeProviderModule = collection[0].class;
						return ClassFactory
							.loadModule(themeProviderModule)
							.then( ThemeProvider => {
								this._themeProvider = APIFactory.createAPI( new ThemeProvider(), [ThemeProviderAPI]);
							})
							.catch( error => this._logger.error(error));
					}
				});

			} catch(e){
				console.log(e);
			}

			return Promise.resolve();

		}

		loadThemeDefinition(themeName) {
			return ClassFactory.loadModule(`text!dashboard-core/js/lib/@waca/dashboard-common/dist/themes/${themeName}.json`)
				.then( JSON.parse.bind(JSON) );
		}

		getThemeDefinition(themeName = 'carbonGrey10') {
			if (this._themes[themeName]) {
				this._themeDefinition = this._themes[themeName];
				return Promise.resolve(this._themes[themeName]);
			}
			return this._themeProvider.loadThemeDefinition(themeName)
				.catch( this._handleLoadingError.bind(this, themeName))
				.then( this._processThemeDefinition.bind(this, themeName))
				.then( themeDefinition => {
					this._setThemeDefinition(themeName, themeDefinition);
					return this._themeDefinition;
				});
		}

		_processThemeDefinition(themeName, contributedThemeDefinition) {
			if( this._themeProvider !== this){
				return this.loadThemeDefinition(themeName)
					.then( this._mergeDeep.bind(this, contributedThemeDefinition));
			}
			return contributedThemeDefinition;
		}

		_handleLoadingError(themeName, error) {
			this._logger.error(error);
			if( this === this._themeProvider) {
				throw error;
			}
			return this.loadThemeDefinition(themeName);
		}

		_isObject(item) {
			return (item && typeof item === 'object' && !Array.isArray(item));
		}

		/**
		 * Merges the contributed theme definition with the default loaded theme definition to fill in missing attributes
		 * Also discards attributes of the contributed theme def if it doesn't match the type with the default theme def
		 * and keeps the attribute from the default theme def
		 * @param {Object} source - the contributed theme definition
		 * @param {Object} target - the default theme that's loaded to fill in missing attributes
		 * @return {Object} the merged object from source and target
		 */
		_mergeDeep(source, target) {
			let output = Object.assign({}, target);
			if (this._isObject(target) && this._isObject(source)) {
				Object.keys(source).forEach(key => {
					if (!source[key]) {
						this._logger.info('No value corresponding to the key: '+ key);
					} else {
						if (!target[key]) {
							Object.assign(output, { [key]: source[key] });
						} else {
							if (typeof source[key] !== typeof target[key]) {
								this._logger.warn('Contributed theme attribute: ' + key +' is not of the right type');
							} else {
								if (this._isObject(source[key])) {
									output[key] = this._mergeDeep(source[key], target[key] );
								} else {
									Object.assign(output, { [key]: source[key] });
								}
							}
						}
					}
				});
			}
			return output;
		}

		_setThemeDefinition(themeName, themeDefinition) {
			this._themes[themeName] = APIFactory.createAPI( new ThemeDefinition(themeDefinition), [ThemeDefinitionAPI]);
			this._themeDefinition = this._themes[themeName];
		}
	}

	return DashboardTheme;
});